// Phase 2.11 — Multi-select owner pill control for assessment findings.
import React, { useState } from "react";
import { Check, ChevronDown, Users, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { FINDING_OWNER_OPTIONS } from "@/configs/findings/ownerOptions";

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

export const OwnerMultiSelect: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  className,
  placeholder = "Select owner(s)",
}) => {
  const [open, setOpen] = useState(false);
  const selected = value ?? [];

  const toggle = (label: string) => {
    if (selected.includes(label)) onChange(selected.filter((v) => v !== label));
    else onChange([...selected, label]);
  };

  const remove = (label: string) => onChange(selected.filter((v) => v !== label));

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {selected.map((label) => (
        <Badge key={label} variant="secondary" className="gap-1 text-[11px]">
          {label}
          {!disabled && (
            <button
              type="button"
              className="rounded-full hover:bg-muted-foreground/20 p-0.5"
              onClick={(e) => { e.stopPropagation(); remove(label); }}
              aria-label={`Remove ${label}`}
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </Badge>
      ))}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className="h-7 text-xs gap-1"
          >
            <Users className="h-3.5 w-3.5" />
            {selected.length === 0 ? placeholder : "Edit"}
            <ChevronDown className="h-3 w-3 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-0 pointer-events-auto" align="start">
          <Command>
            <CommandInput placeholder="Filter…" />
            <CommandList>
              <CommandEmpty>No owners found.</CommandEmpty>
              <CommandGroup>
                {FINDING_OWNER_OPTIONS.map((label) => {
                  const isOn = selected.includes(label);
                  return (
                    <CommandItem
                      key={label}
                      value={label}
                      onSelect={() => toggle(label)}
                      className="text-xs"
                    >
                      <div className={cn(
                        "mr-2 h-4 w-4 rounded-sm border flex items-center justify-center",
                        isOn ? "bg-primary border-primary text-primary-foreground" : "border-muted-foreground/40"
                      )}>
                        {isOn && <Check className="h-3 w-3" />}
                      </div>
                      {label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
